import { useEffect } from 'react';
import { SidePanel } from '@getsynapse/design-system';
import { useDispatch, useSelector } from 'react-redux';
import { chartDataSelectors } from 'state/normalized/insights/chartDataSlice';
import {
  getActiveDrilldownData,
  setActiveDrilldownId,
  setActiveDrilldownKey,
} from 'state/normalized/insightDrilldownMetadata/insightDrilldownMetadataSlice';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import {
  DrillDownCategory,
  InsightCatalogue,
} from 'types/store/normalized/insights/InsightCatalogue';
import { PATHS } from 'utils/constants';
import RequestDrillDownTable from 'Organisms/DashboardDrilldown/RequestDrillDownTable/RequestDrillDownTable';
import ProjectDrillDownTable from 'Organisms/DashboardDrilldown/ProjectDrillDownTable/ProjectDrillDownTable';
import TaskDrillDownTable from 'Organisms/DashboardDrilldown/TaskDrillDownTable/TaskDrillDownTable';
import UserDrillDownTable from 'Organisms/DashboardDrilldown/UserDrillDownTable/UserDrillDownTable';
import CapacityManagementDrillDownTable from 'Organisms/DashboardDrilldown/CapacityManagementDrillDownTable/CapacityManagementDrillDownTable';
import EventDrillDownTable from 'Organisms/DashboardDrilldown/EventDrillDownTable/EventDrillDownTable';
import { useDrillDown } from '../InsightsGrid/hooks/useDrillDown';

const DrillDownSidePanel = () => {
  const dispatch = useDispatch();
  const match = useRouteMatch<{
    dashboardId: string;
    entityType: DrillDownCategory;
    insightId: InsightCatalogue;
    drilldownKey?: string;
  }>(PATHS.DASHBOARD_DRILLDOWN_PAGE);

  const shouldRender = !!match?.params.insightId;

  const { closeDrillDown } = useDrillDown();

  const drilldownLink = useSelector(
    chartDataSelectors.selectDrilldownLinkByInsightId(
      match?.params.insightId,
      match?.params.drilldownKey
    )
  );

  useEffect(() => {
    dispatch(setActiveDrilldownId(match?.params.insightId));
    dispatch(setActiveDrilldownKey(match?.params.drilldownKey));
    return () => {
      dispatch(setActiveDrilldownId());
      dispatch(setActiveDrilldownKey());
    };
  }, [match?.params.insightId, match?.params.drilldownKey, dispatch]);

  useEffect(() => {
    if (drilldownLink && shouldRender) {
      dispatch(getActiveDrilldownData(drilldownLink));
    }
  }, [drilldownLink, dispatch, shouldRender]);

  const onClose = () => {
    closeDrillDown();
  };

  return shouldRender ? (
    <SidePanel
      className='w-content'
      isOpen={Boolean(match?.params.insightId)}
      onClose={onClose}
      footerButtons={[]}
    >
      <Switch>
        <Route
          path={PATHS.DASHBOARD_DRILLDOWN_PAGE.replace(
            ':entityType',
            DrillDownCategory.REQUESTS
          )}
        >
          <RequestDrillDownTable />
        </Route>

        <Route
          path={PATHS.DASHBOARD_DRILLDOWN_PAGE.replace(
            ':entityType',
            DrillDownCategory.PROJECTS
          )}
        >
          <ProjectDrillDownTable />
        </Route>

        <Route
          path={PATHS.DASHBOARD_DRILLDOWN_PAGE.replace(
            ':entityType',
            DrillDownCategory.TASKS
          )}
        >
          <TaskDrillDownTable />
        </Route>

        <Route
          path={PATHS.DASHBOARD_DRILLDOWN_PAGE.replace(
            ':entityType',
            DrillDownCategory.USERS
          )}
        >
          <UserDrillDownTable />
        </Route>

        <Route
          path={PATHS.DASHBOARD_DRILLDOWN_PAGE.replace(
            ':entityType',
            DrillDownCategory.CAPACITY_MANAGEMENT
          )}
        >
          <CapacityManagementDrillDownTable />
        </Route>

        <Route
          path={PATHS.DASHBOARD_DRILLDOWN_PAGE.replace(
            ':entityType',
            DrillDownCategory.EVENTS
          )}
        >
          <EventDrillDownTable />
        </Route>
      </Switch>
    </SidePanel>
  ) : (
    <></>
  );
};

export default DrillDownSidePanel;
